import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import SplashScreen from "./SplashScreen";

/**
 * SplashGate — pre-launch video splash screen.
 *
 *   • Public routes see the splash video until the visitor clicks the DOOZYGO
 *     logo hotspot. Once clicked, the gate stores a sessionStorage flag so the
 *     visitor isn't re-shown the splash while browsing other public pages
 *     during the same browser session. A fresh session (new tab after close)
 *     shows the splash again — this matches the "pre-launch gate" intent.
 *
 *   • Internal / deep-link routes always bypass the splash so admin, ops,
 *     vendors, and customers landing via tracking/RSVP/share links keep
 *     working. If this list changes, update BYPASS_PREFIXES below.
 *
 *   • `?relaunch=1` on any URL forces the splash back (useful for QA / demos).
 *     `?enter=1` on any URL skips the splash permanently for this session.
 */
const SESSION_FLAG = "doozygo_splash_entered";

// Routes that must NEVER be gated — business keeps running during pre-launch.
const BYPASS_PREFIXES = [
  "/admin",
  "/ops",
  "/vendor-portal",
  "/vendor-register",
  "/decorator-registration",
  "/track",
  "/booking",
  "/cancel-booking",
  "/pool",
  "/rsvp",
  "/field-tracker",
  "/magic",
  "/my-account",
  "/r/",
  "/privacy-policy",
  "/terms-of-service",
];

const isBypassedRoute = (pathname) =>
  BYPASS_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p));

const SplashGate = ({ children }) => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();

  // Resolve the initial state once per mount — any later navigations should
  // never pop the splash back up without an explicit ?relaunch=1.
  const [showSplash, setShowSplash] = useState(() => {
    try {
      if (typeof window === "undefined") return false;
      if (params.get("relaunch") === "1") {
        window.sessionStorage.removeItem(SESSION_FLAG);
        return true;
      }
      if (params.get("enter") === "1") {
        window.sessionStorage.setItem(SESSION_FLAG, "1");
        return false;
      }
      if (isBypassedRoute(pathname)) return false;
      return window.sessionStorage.getItem(SESSION_FLAG) !== "1";
    } catch (_) {
      return false;
    }
  });

  // If the user navigates INTO a bypassed route while the splash is already up
  // (e.g. by typing /admin into the address bar), get out of their way.
  useEffect(() => {
    if (showSplash && isBypassedRoute(pathname)) {
      setShowSplash(false);
    }
  }, [pathname, showSplash]);

  const handleEnter = useCallback(() => {
    try {
      window.sessionStorage.setItem(SESSION_FLAG, "1");
    } catch (_) { /* ignore */ }
    setShowSplash(false);
  }, []);

  return (
    <>
      {children}
      {showSplash && <SplashScreen onEnter={handleEnter} />}
    </>
  );
};

export default SplashGate;
